<template>
    <div v-if="displayOrderTracker()" class="order-tracker-container" :class="style()">
		<div v-if="minimalTracker" class="minimal-tracker-wrapper">
			<order-tracker-minimal />
		</div>
        <div v-if="!minimalTracker && activeOrder" id="order-tracker-body" tabindex="0" aria-label="order tracker view">
            <div class="container-title">Order {{displayOrderId}} in progress</div> 
			<order-tracker
                :orderLocation="location"
                :order="activeOrder" />
		</div>
		<div v-if="activeOrder && !hideOrderDetailLink && !forceShow" class="order-view-detail" :class="detailStyle()">
			<a href="/?order-details"
				aria-label="view order details"
				class="store-navigation-color"
				@click.prevent="navigateToOrderConfirmation">
				View Details<span v-if="minimalTracker"> for Order &num;{{displayOrderId}}</span>
			</a>
		</div>
	</div>
</template>

<script>
import * as moment from "moment";
import EventBus from '@EventBus';
import { mapGetters, mapActions } from "vuex";
import { LOCATION, RECENT_ORDERS } from "@/stores/defs";
import DateManipulator from '@/utils/DateManipulator';

import { EServiceType } from "@/models/interfaces/Orders/EServiceType";
import EOrderStatus from "@/models/interfaces/Orders/EOrderStatus";

import orderService from "@/services/order.service";
import recentOrderService from "@/services/recent.orders.service";

import RecentOrderModel from "@/models/RecentOrderModel";
import OrderModel from "@/models/OrderModel";

import orderMixin from "@Mixin/orderMixin";
import upsellMixin from "@Mixin/upsellMixin";
import trackerMixin from "@Mixin/trackerMixin";
import routeMixin from "@Mixin/routeMixin";
import navigationMixin from "@Mixin/navigationMixin";

import OrderTracker from "@/components/orders/OrderTracker.vue";
import OrderTrackerMinimal from '@OrderConfirmation/OrderTrackerMinimal';

export default {
    mixins: [
		orderMixin,
		upsellMixin,
		trackerMixin,
		routeMixin,
		navigationMixin,
	],
	props: {
		forceShow: {
			type: Boolean,
			default: false,
		},
	},
    components: {
		"order-tracker": OrderTracker,
		'order-tracker-minimal': OrderTrackerMinimal,
	},
    data() {
        return {
            order: null,
			EOrderStatus: EOrderStatus,
			displayTracker: false,
			minimalTracker: false,
        };
	},
	computed: {
		...mapGetters({
			location: LOCATION.GET_LOCATION,
			recentOrders: RECENT_ORDERS.GET_RECENT_ORDERS,
			activeOrder: RECENT_ORDERS.GET_ACTIVE_ORDER
		}),
		hideOrderDetailLink() {
			const hiddenForRoutes = [
				'order-confirmation-mobile'
			];
			return this.whiteListRoute(hiddenForRoutes);
		},
    },
    methods: {
        navigateToOrderConfirmation() {
			this.setShowUpsellOff();
			const isMobile = (this.$mq !== 'desktop');
			const route = this._getOrderConfirmationRoute(isMobile);
			this._navigateToRouteObject(route);
        },
        ...mapActions({
            setActiveOrder: RECENT_ORDERS.SET_ACTIVE_ORDER
		}),
		animateOpen() {
			const target = this.$el;
			const isActive = this.getTrackerActive;
			const desktopSidebar = !this.$isMobile && this.$isSidebar;
			if (target.nodeName === '#comment' || !isActive || desktopSidebar) {
				return;
			}
			// TODO heights need to be more dynamic
			const trackerHeight = (this.$mq === 'mobile') ? '120px' : '240px';
			const actualHeight = (this.$mq === 'mobile') ? '210px' : '240px';
			const padding = (this.$mq === 'mobile') ? '0' : '1em 0';
			target.style.height = actualHeight;
			this.$anime({
				targets: target,
				duration: 500,
				delay: 333,
				opacity: 1,
				padding: padding,
				easing: 'easeInOutQuad',
			});
			EventBus.$emit('order-tracker-is-displayed', trackerHeight);
		},
		displayOrderTracker() {
			this.displayTracker = false;
			const hiddenForRoutes = [
				'greeting',
				'service',
				'locator',
				'checkout',
				'category',
				'menu',
				'payment-method',
				'account-sign-up',
				'account-sign-in',
				'account-guest-form',
				'account-enter-pin',
			];
			if (this.forceShow) {
				this.displayTracker = true;
				this.setTrackerActive(true);
				return true;
			}
			if (this.whiteListRoute(hiddenForRoutes)) {
				this.setTrackerActive(false);
				return false;
			}
			if (this._isOrderConfirmation) {
				this.displayTracker = true;
				this.setTrackerActive(true);
				return true;
			}
			let activeOrder = this.activeOrder;
			if (!activeOrder || !this.location) {
				this.setTrackerActive(false);
				return false;
			}
			const isOrder = (activeOrder instanceof OrderModel || activeOrder instanceof RecentOrderModel);
			if (!isOrder) {
				activeOrder = new OrderModel(activeOrder);
			}
            const isFromToday = DateManipulator.isMomentToday(moment(activeOrder.purchaseDate));
            const displayStatus = (activeOrder.status !== EOrderStatus.STATUS_DELIVERED);

			const isDisplayed = (isFromToday && displayStatus);
			this.displayTracker = isDisplayed;
			this.setTrackerActive(isDisplayed);
			return isDisplayed;
		},
		style() {
			const hideBorder = this.whiteListRoute(['order']);
			return {
				'is-minimal': this.minimalTracker,
				'hide-border': hideBorder,
			};
		},
		detailStyle() {
			return {
				'use-margin': this.minimalTracker,
			};
		},
	},
	updated() {
		EventBus.$emit('recalculate-header');
	},
	mounted() {
		if (this.displayTracker) {
			this.animateOpen();
		}
	},
	created() {
		this.minimalTracker = this.$isMinimal;
	},
	watch: {
		displayTracker(newValue, oldValue) {
			if (newValue) {
				this.animateOpen();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import "globalStyles";

.order-tracker-container {
    position: relative;
    margin: auto;
	padding: 0;

	width: 100%;
	height: 0; // Initial height is 0
	max-height: $orderTrackerDesktopHeight;
	overflow: hidden;
	opacity: 0; //initial

	background-color: white;
	&.is-minimal {
		display: flex;
		flex-direction: column-reverse;
		justify-content: flex-end;
		border-bottom: 1px solid $greyOutline;

		max-height: 148px;
		height: 120px;

		opacity: 1;
	}
}

#order-tracker-body {
    max-width: $maxWidthContainerDesktop;
    margin: auto;
}

.order-view-detail {
    width: 100%;
    text-align: center;
    font-weight: bold;
	&.use-margin {
		margin-top: 10px;
		font-size: 14px;
	}
}

.minimal-tracker-wrapper {
	display: flex;
	justify-content: center;
	align-items: flex-start;
	margin-top: 2em;
}

@include desktop {
    .order-view-detail {
        position: relative;
		top: -1rem;
	}
	.order-tracker-container {
		&.is-minimal {
			max-height: 20vh;
			height: 20vh;
			.order-view-detail {
				margin-top: 1em;
				font-size: 1.5em;
			}
			.minimal-tracker-wrapper {
				flex: 1;
				margin-top: 0;
			}
		}
	}
}

@include mobile {
    .landing-container {
        margin-top: 1rem;
        padding-bottom: 4vh;
    }

    #order-tracker-body {
        padding-top: 0.3rem;
        text-align: center;
    }
    .order-tracker-container {
        max-height: $orderTrackerMobileHeight;
        padding: 0;

        border-bottom: 1px solid $greyOutline;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/orders/OrderTrackerContainer.vue